// Firefox copies the whole hit canvas on every 1px getImageData, so where that read is slow the
// hit pixels are copied once per hit redraw and reused until the next one.
import Konva from "konva";

const SLOW_PIXEL_READ_MS = 1;
const CONSECUTIVE_SLOW_READS_BEFORE_CACHING = 3;
const MAX_COPY_COST_IN_SLOW_READS = 3;

const markHitDirty = (node) => {
  const layer = node.getLayer?.();
  if (!layer) return;
  layer._hitPixels = null;
  layer._hitPixelReadsSinceDraw = 0;
};

const wrapToMarkHitDirty = (proto, method) => {
  const original = proto[method];
  proto[method] = function (...args) {
    markHitDirty(this);
    return original.apply(this, args);
  };
};

const stopCaching = (layer) => {
  layer._hitPixels = null;
  layer._slowHitPixelStreak = 0;
  layer._hitPixelCacheOn = false;
};

const readCachedHitPixel = (layer, x, y) => {
  const { _canvas: canvas, context } = layer.hitCanvas;
  const { width, height } = canvas;
  let cached = layer._hitPixels;
  if (!cached || cached.width !== width || cached.height !== height) {
    const startedAt = performance.now();
    cached = { width, height, data: context.getImageData(0, 0, width, height).data };
    const copyMs = performance.now() - startedAt;
    if (copyMs > MAX_COPY_COST_IN_SLOW_READS * layer._slowHitPixelReadMs) {
      stopCaching(layer);
      return null;
    }
    layer._hitPixels = cached;
  }
  const offset = (y * width + x) * 4;
  return cached.data.subarray(offset, offset + 4);
};

const readDirectHitPixel = (layer, x, y) => {
  const startedAt = performance.now();
  const pixel = layer.hitCanvas.context.getImageData(x, y, 1, 1).data;
  const readMs = performance.now() - startedAt;
  const isFirstReadAfterDraw = !layer._hitPixelReadsSinceDraw;
  layer._hitPixelReadsSinceDraw = (layer._hitPixelReadsSinceDraw || 0) + 1;
  if (isFirstReadAfterDraw) return pixel;

  if (readMs <= SLOW_PIXEL_READ_MS) {
    layer._slowHitPixelStreak = 0;
    return pixel;
  }
  layer._slowHitPixelStreak = (layer._slowHitPixelStreak || 0) + 1;
  layer._slowHitPixelReadMs = readMs;
  if (layer._slowHitPixelStreak >= CONSECUTIVE_SLOW_READS_BEFORE_CACHING) {
    layer._hitPixelCacheOn = true;
  }
  return pixel;
};

const readHitPixel = (layer, x, y) => {
  const { width, height } = layer.hitCanvas._canvas;
  if (x < 0 || y < 0 || x >= width || y >= height) return null;
  if (layer._hitPixelCacheOn) {
    const pixel = readCachedHitPixel(layer, x, y);
    if (pixel) return pixel;
  }
  return readDirectHitPixel(layer, x, y);
};

if (!Konva.Layer.prototype._hitPixelCacheInstalled) {
  wrapToMarkHitDirty(Konva.Layer.prototype, "drawHit");
  wrapToMarkHitDirty(Konva.Container.prototype, "drawHit");
  wrapToMarkHitDirty(Konva.Shape.prototype, "drawHit");
  wrapToMarkHitDirty(Konva.Layer.prototype, "clear");
  wrapToMarkHitDirty(Konva.Layer.prototype, "setSize");

  Konva.Layer.prototype._getIntersection = function (pos) {
    const ratio = this.hitCanvas.pixelRatio;
    const pixel = readHitPixel(this, Math.round(pos.x * ratio), Math.round(pos.y * ratio));
    if (!pixel) return {};

    const alpha = pixel[3];
    if (alpha === 255) {
      const colorKey = Konva.Util._rgbToHex(pixel[0], pixel[1], pixel[2]);
      const shape = Konva.shapes[`#${colorKey}`];
      return shape ? { shape } : { antialiased: true };
    }
    return alpha > 0 ? { antialiased: true } : {};
  };

  Konva.Layer.prototype._hitPixelCacheInstalled = true;
}
